import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { checkSubdomainAvailability, checkCustomDomainAvailability, getCustomDomainInstructions } from '../../../services/projects';
import config from '../../../config';

const { baseDomain, altDomain, cdnCname, proxyIp } = config;

const DomainSection = ({
  formData,
  errors,
  onChange,
  onCheckDomain,
  projectId
}) => {
  const [isChecking, setIsChecking] = useState(false);
  const [domainStatus, setDomainStatus] = useState(null);
  const [customDomainStatus, setCustomDomainStatus] = useState(null);
  const [showDnsInstructions, setShowDnsInstructions] = useState(false);

  const domainTypeOptions = [
    { value: 'subdomain', label: 'Поддомен' },
    { value: 'custom', label: 'Собственный домен' }
  ];

  const baseDomainOptions = [
    { value: baseDomain, label: baseDomain },
    { value: altDomain, label: altDomain }
  ];

  // Get currently selected base domain (default to baseDomain)
  const selectedBaseDomain = formData?.selectedDomain || baseDomain;

  const handleCheckSubdomain = async () => {
    if (!formData?.subdomain) return;

    setIsChecking(true);
    setDomainStatus(null);

    try {
      const result = await checkSubdomainAvailability(formData.subdomain, projectId);
      setDomainStatus(result);
    } catch (error) {
      console.error('checkSubdomainAvailability error:', error);
      setDomainStatus({
        available: false,
        message: 'Ошибка проверки. Попробуйте позже.'
      });
    } finally {
      setIsChecking(false);
    }
  };

  const handleCheckCustomDomain = async () => {
    if (!formData?.customDomain) return;

    setIsChecking(true);
    setCustomDomainStatus(null);

    try {
      const result = await checkCustomDomainAvailability(formData.customDomain, projectId);
      setCustomDomainStatus(result);
      if (result.available) {
        setShowDnsInstructions(true);
      }
    } catch (error) {
      console.error('checkCustomDomainAvailability error:', error);
      setCustomDomainStatus({
        available: false,
        message: 'Ошибка проверки. Попробуйте позже.'
      });
    } finally {
      setIsChecking(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg md:text-xl font-semibold text-foreground mb-4">
          Домен и публикация
        </h3>
        <p className="text-sm text-text-secondary mb-6">
          Настройте адрес вашего сайта
        </p>
      </div>
      <Select
        label="Тип домена"
        options={domainTypeOptions}
        value={formData?.domainType}
        onChange={(value) => onChange({ target: { name: 'domainType', value } })}
        error={errors?.domainType}
        description="Выберите, как будет доступен ваш сайт"
        className="mb-4"
      />
      {formData?.domainType === 'subdomain' && (
        <div className="bg-muted p-4 md:p-6 rounded-lg space-y-4">
          <Select
            label="Базовый домен"
            options={baseDomainOptions}
            value={selectedBaseDomain}
            onChange={(value) => onChange({ target: { name: 'selectedDomain', value } })}
            description="Выберите домен для вашего сайта"
            className="mb-4"
          />

          <Input
            label="Поддомен"
            type="text"
            name="subdomain"
            placeholder="myspace"
            value={formData?.subdomain}
            onChange={onChange}
            error={errors?.subdomain}
            description={`Ваш сайт будет доступен по адресу: myspace.${selectedBaseDomain}`}
          />

          {formData?.subdomain && (
            <div className="flex items-center gap-2 p-3 bg-background rounded-lg">
              <Icon name="Globe" size={20} className="text-primary" />
              <a
                href={`https://${formData.subdomain}.${selectedBaseDomain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-primary hover:underline"
              >
                {formData.subdomain}.{selectedBaseDomain}
              </a>
            </div>
          )}

          <Button
            variant="outline"
            iconName="CheckCircle"
            iconPosition="left"
            onClick={handleCheckSubdomain}
            loading={isChecking}
            disabled={!formData?.subdomain}
          >
            Проверить доступность
          </Button>

          {domainStatus && (
            <div className={`flex items-start gap-3 p-3 rounded-lg ${
              domainStatus?.available ? 'bg-success/10' : 'bg-error/10'
            }`}>
              <Icon 
                name={domainStatus?.available ? 'CheckCircle' : 'XCircle'} 
                size={20} 
                className={domainStatus?.available ? 'text-success' : 'text-error'}
              />
              <p className="text-sm">{domainStatus?.message}</p>
            </div>
          )}
        </div>
      )}
      {formData?.domainType === 'custom' && (
        <div className="bg-muted p-4 md:p-6 rounded-lg space-y-4">
          <Input
            label="Собственный домен"
            type="text"
            name="customDomain"
            placeholder="mysite.ru"
            value={formData?.customDomain}
            onChange={onChange}
            error={errors?.customDomain}
            description="Введите домен без http:// и www (например: mysite.ru)"
          />

          <Button
            variant="outline"
            iconName="CheckCircle"
            iconPosition="left"
            onClick={handleCheckCustomDomain}
            loading={isChecking}
            disabled={!formData?.customDomain}
          >
            Проверить доступность
          </Button>

          {customDomainStatus && (
            <div className={`flex items-start gap-3 p-3 rounded-lg ${
              customDomainStatus?.available ? 'bg-success/10' : 'bg-error/10'
            }`}>
              <Icon
                name={customDomainStatus?.available ? 'CheckCircle' : 'XCircle'}
                size={20}
                className={customDomainStatus?.available ? 'text-success' : 'text-error'}
              />
              <p className="text-sm">{customDomainStatus?.message}</p>
            </div>
          )}

          {(showDnsInstructions || formData?.customDomain) && (
            <div className="bg-background p-4 rounded-lg space-y-4">
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Settings" size={16} />
                Настройка DNS
              </h4>

              <p className="text-xs md:text-sm text-text-secondary">
                Добавьте одну из DNS записей у вашего регистратора доменов:
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-xs md:text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 text-text-secondary font-medium">Вариант</th>
                      <th className="text-left py-2 px-3 text-text-secondary font-medium">Тип</th>
                      <th className="text-left py-2 px-3 text-text-secondary font-medium">Имя</th>
                      <th className="text-left py-2 px-3 text-text-secondary font-medium">Значение</th>
                      <th className="py-2 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-border">
                      <td className="py-2 px-3 text-text-secondary">Для поддоменов (www)</td>
                      <td className="py-2 px-3 font-mono">CNAME</td>
                      <td className="py-2 px-3 font-mono">{formData?.customDomain ? `www.${formData.customDomain}` : 'www.ваш-домен'}</td>
                      <td className="py-2 px-3 font-mono text-primary break-all">{cdnCname}</td>
                      <td className="py-2 px-3">
                        <button
                          onClick={() => copyToClipboard(cdnCname)}
                          className="p-1 hover:bg-muted rounded transition-colors"
                          title="Скопировать"
                        >
                          <Icon name="Copy" size={14} className="text-text-secondary" />
                        </button>
                      </td>
                    </tr>
                    <tr className="border-b border-border bg-success/5">
                      <td className="py-2 px-3 text-text-secondary">Для корневого домена</td>
                      <td className="py-2 px-3 font-mono">A</td>
                      <td className="py-2 px-3 font-mono">{formData?.customDomain || 'ваш-домен'}</td>
                      <td className="py-2 px-3 font-mono text-primary">{proxyIp}</td>
                      <td className="py-2 px-3">
                        <button
                          onClick={() => copyToClipboard(proxyIp)}
                          className="p-1 hover:bg-muted rounded transition-colors"
                          title="Скопировать"
                        >
                          <Icon name="Copy" size={14} className="text-text-secondary" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="flex items-start gap-2 p-3 bg-accent/10 rounded-lg">
                <Icon name="Info" size={16} className="text-accent flex-shrink-0 mt-0.5" />
                <p className="text-xs text-text-secondary">
                  <strong>Рекомендуем A запись</strong> — работает для любых доменов.
                  CNAME подходит только для поддоменов (www, shop и т.д.), но не для корневых доменов (@).
                </p>
              </div>

              <p className="text-xs text-text-secondary">
                После добавления DNS записей изменения вступят в силу в течение 24-48 часов.
              </p>
            </div>
          )}
        </div>
      )}
      <div className="border-t border-border pt-6">
        <div className="flex items-start gap-3 p-4 bg-accent/10 rounded-lg">
          <Icon name="Lock" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">SSL сертификат</p>
            <p className="text-xs md:text-sm text-text-secondary">
              Автоматически выдается для всех доменов. Ваш сайт будет доступен по защищенному протоколу HTTPS.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DomainSection;